// utils/imageUtils.js
import { DEFAULT_PLACEHOLDER } from './imageFallback';

/**
 * Returns a proxied image URL if needed, otherwise returns the original URL.
 * If you don't use an image proxy, just return the original URL.
 * This prevents "getProxiedImageUrl is not a function" errors.
 */
export function getProxiedImageUrl(url) {
  if (!url) return '';
  // Example: If you use a proxy service, modify this line:
  // return `https://your-image-proxy.com?url=${encodeURIComponent(url)}`;
  return url;
}

/**
 * Decode HTML entities in a string (fixes &#x2F; to /)
 * @param {string} str - String with HTML entities
 * @returns {string} - Decoded string
 */
const decodeHTMLEntities = (str) => {
  if (!str || typeof str !== 'string') return str;

  // Decode common HTML entities
  return str
    .replace(/&#x2F;/g, '/')
    .replace(/&#47;/g, '/')
    .replace(/&sol;/g, '/')
    .replace(/&#x2B;/g, '+')
    .replace(/&#43;/g, '+')
    .replace(/&plus;/g, '+')
    .replace(/&#x3D;/g, '=')
    .replace(/&#61;/g, '=')
    .replace(/&equals;/g, '=');
};

/**
 * Get the best image URL for a product, preferring thumbnails for performance
 * @param {Object} product - Product object with images array
 * @param {boolean} preferThumbnail - Whether to prefer thumbnail over full image
 * @param {string} fallback - Fallback image URL
 * @returns {string} - Best available image URL
 */
export const getProductImageUrl = (product, preferThumbnail = true, fallback = DEFAULT_PLACEHOLDER) => {
  const fallbackImage = fallback;

  if (!product || typeof product !== 'object') {
    return fallbackImage;
  }

  if (!Array.isArray(product.images) || product.images.length === 0) {
    return fallbackImage;
  }

  let primaryImage = product.images.find(img => img && img.isPrimary) || product.images[0];

  if (!primaryImage) {
    return fallbackImage;
  }

  let imageUrl = preferThumbnail ? primaryImage.thumbnail : primaryImage.url;

  if (!imageUrl) {
    imageUrl = primaryImage.url || primaryImage.thumbnail;
  }

  if (!imageUrl) {
    return fallbackImage;
  }

  // Decode HTML entities in the URL
  return decodeHTMLEntities(imageUrl);
};

/**
 * Get thumbnail URL specifically
 * @param {Object} product - Product object with images array
 * @param {string} fallback - Fallback image URL
 * @returns {string} - Thumbnail URL
 */
export const getProductThumbnailUrl = (product, fallback = DEFAULT_PLACEHOLDER) => {
  if (!product) {
    return fallback;
  }

  let imageUrl = null;

  // Check different possible image properties
  // 1. Check for images array
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const primaryImage = product.images.find(img => img && img.isPrimary) || product.images[0];

    if (primaryImage && (primaryImage.thumbnail || primaryImage.url)) {
      imageUrl = primaryImage.thumbnail || primaryImage.url;
    }
  }

  // 2. Check for direct image property (common in some APIs)
  if (!imageUrl && product.image && product.image !== '') {
    imageUrl = product.image;
  }

  // 3. Check for thumbnail property
  if (!imageUrl && product.thumbnail && product.thumbnail !== '') {
    imageUrl = product.thumbnail;
  }

  // 4. Check for imageUrl property
  if (!imageUrl && product.imageUrl && product.imageUrl !== '') {
    imageUrl = product.imageUrl;
  }

  // 5. Check for featuredImage property
  if (!imageUrl && product.featuredImage && product.featuredImage !== '') {
    imageUrl = product.featuredImage;
  }

  // Decode HTML entities if we have an image URL
  if (imageUrl) {
    return decodeHTMLEntities(imageUrl);
  }

  // Return null if fallback is null (for explicit null checking), otherwise return fallback
  return fallback;
};

/**
 * Get full image URL specifically
 * @param {Object} product - Product object with images array
 * @param {string} fallback - Fallback image URL
 * @returns {string} - Full image URL
 */
export const getProductFullImageUrl = (product, fallback = DEFAULT_PLACEHOLDER) => {
  return getProductImageUrl(product, false, fallback);
};



/**
 * Generate a low-quality placeholder for blur effect
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {string} - Base64 encoded placeholder
 */
export const generateBlurDataURL = (width = 10, height = 10) => {
  // Create a simple low-res placeholder
  const canvas = typeof window !== 'undefined' ? document.createElement('canvas') : null;
  if (!canvas) {
    // Fallback base64 for SSR
    return "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=";
  }
  
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Create a subtle gradient
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#f3f4f6');
  gradient.addColorStop(1, '#e5e7eb');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  return canvas.toDataURL('image/jpeg', 0.1);
};

/**
 * Get responsive image sizes based on context
 * @param {string} context - Context where image is used ('card', 'hero', 'thumbnail', 'detail')
 * @returns {string} - Sizes attribute value
 */
export const getResponsiveSizes = (context = 'card') => {
  const sizeConfigs = {
    card: "(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw",
    hero: "(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 60vw",
    thumbnail: "(max-width: 640px) 20vw, (max-width: 768px) 15vw, 10vw",
    detail: "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 40vw",
    avatar: "64px",
    icon: "32px"
  };
  
  return sizeConfigs[context] || sizeConfigs.card;
};

/**
 * Enhanced image error handler with retry strategies
 * @param {string} imageUrl - Original image URL that failed
 * @param {number} retryAttempt - Current retry attempt (0-based)
 * @returns {string|null} - Alternative URL to try, or null if no more options
 */
export const getImageRetryUrl = (imageUrl, retryAttempt = 0) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return null;
  }

  const strategies = [
    // Strategy 1: Try building full URL if it's a relative path
    (url) => {
      if (url.startsWith('/uploads/')) {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000';
        return `${apiUrl}${url}`;
      }
      return null;
    },
    
    // Strategy 2: Try thumbnail version if it's a full image
    (url) => {
      if (url.includes('/uploads/products/') && !url.includes('thumb-')) {
        const fileName = url.split('/').pop();
        const dirPath = url.substring(0, url.lastIndexOf('/'));
        return `${dirPath}/thumb-${fileName}`;
      }
      return null;
    },
    
    // Strategy 3: No proxy needed - Cloudinary has CORS enabled
    (url) => {
      return null; // No additional strategies needed
    }
  ];

  if (retryAttempt < strategies.length) {
    const strategy = strategies[retryAttempt];
    return strategy(imageUrl);
  }

  return null; // No more retry strategies
};

/**
 * Validate if an image URL is potentially valid
 * @param {string} url - Image URL to validate
 * @returns {boolean} - Whether the URL appears valid
 */
export const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    // Check for valid URL format
    if (url.startsWith('http://') || url.startsWith('https://')) {
      new URL(url); // This will throw if URL is invalid
    }
    
    // Check for valid file extensions
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const hasValidExtension = validExtensions.some(ext => 
      url.toLowerCase().includes(ext)
    );
    
    // Check for valid image patterns
    const isPlaceholder = url.includes('placeholder') || url.includes('placehold');
    const isUploadPath = url.includes('/uploads/');
    const isBase64 = url.startsWith('data:image/');
    
    return hasValidExtension || isPlaceholder || isUploadPath || isBase64;
  } catch (error) {
    return false;
  }
};

/**
 * Build a normalized image URL from potentially malformed URLs
 * Handles Cloudinary URLs, relative paths, and data URLs
 * @param {string} imageUrl - Original image URL
 * @returns {string} - Normalized image URL
 */
export const buildImageUrl = (imageUrl) => {
  // Return placeholder if no URL provided
  if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') {
    return DEFAULT_PLACEHOLDER;
  }

  // Return data URLs as-is (base64 images)
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // Return full HTTP/HTTPS URLs as-is (Cloudinary, external CDNs)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return decodeHTMLEntities(imageUrl);
  }

  // Handle relative paths - convert to absolute
  if (imageUrl.startsWith('/')) {
    // Already absolute path
    return decodeHTMLEntities(imageUrl);
  }

  // Handle relative paths without leading slash
  return decodeHTMLEntities(`/${imageUrl}`);
};

/**
 * Get optimized image URL with format and quality parameters
 * @param {string} imageUrl - Original image URL
 * @param {Object} options - Optimization options
 * @returns {string} - Optimized image URL
 */
export const getOptimizedImageUrl = (imageUrl, options = {}) => {
  const {
    width = null,
    height = null,
    quality = 75,
    format = 'webp'
  } = options;

  if (!imageUrl || !isValidImageUrl(imageUrl)) {
    return DEFAULT_PLACEHOLDER;
  }

  // For local uploads, we can add query parameters for optimization
  if (imageUrl.includes('/uploads/') && (width || height || quality !== 75)) {
    const url = new URL(imageUrl, window.location.origin);
    if (width) url.searchParams.set('w', width.toString());
    if (height) url.searchParams.set('h', height.toString());
    if (quality !== 75) url.searchParams.set('q', quality.toString());
    if (format !== 'webp') url.searchParams.set('f', format);
    return url.toString();
  }

  return imageUrl;
};

/**
 * Preload critical images for better performance
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @param {Object} options - Preload options
 */
export const preloadImages = (imageUrls, options = {}) => {
  const { priority = false, timeout = 5000 } = options;
  
  if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
    return Promise.resolve([]);
  }

  const loadPromises = imageUrls.map(url => {
    return new Promise((resolve) => {
      if (!isValidImageUrl(url)) {
        resolve({ url, success: false, error: 'Invalid URL' });
        return;
      }

      const img = new Image();
      const timeoutId = setTimeout(() => {
        resolve({ url, success: false, error: 'Timeout' });
      }, timeout);

      img.onload = () => {
        clearTimeout(timeoutId);
        resolve({ url, success: true });
      };

      img.onerror = (error) => {
        clearTimeout(timeoutId);
        resolve({ url, success: false, error: error.message || 'Load failed' });
      };

      img.src = url;
    });
  });

  return Promise.all(loadPromises);
};
